import React, { useState, useEffect } from "react";
import { Card, Table, Select, Button, Modal, Space, Spin, message } from "antd";
import { DownloadOutlined } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";

import { getOnboardingByApplicant } from "Services/AdminServices/OnBoardingService";
import generateOnboardingPDF from "FileGenerator/AdminFileGenerator/OnboardingReport";

const { Option } = Select;

function ReceivedDocuments() {
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchDepartments();
    fetchEmployees(null, true);
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await axiosInstance.get(API.department.getList);
      setDepartments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchEmployees = async (departmentId = null, hasDocuments = true) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(API.employee.getAll(departmentId, hasDocuments));
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- DOCUMENTS ----------------
  const handleViewDocuments = async (employeeId) => {
    setDocLoading(true);
    try {
      const res = await axiosInstance.get(API.hiringContracts.getByUserId(employeeId));

      if (res?.data?.detail) {
        message.error(res.data.detail);
        setDocLoading(false);
        return;
      }

      const data = res.data;

      // Convert API data into displayable format
      const documentList = [
        { name: "NDA File", file: data.ndafile },
        { name: "NCA File", file: data.ncafile },
        { name: "Employee Agreement", file: data.employeeAgreement },
        { name: "I-9 Form", file: data.i_nine },
        { name: "W-4 Form", file: data.w_four },
        { name: "Offer Letter", file: data.offerLetter },
        { name: "HR Policy", file: data.hrPolicy },
        { name: "Resume", file: data.resume },
      ].filter((doc) => doc.file); // Only non-empty docs

      setDocuments(documentList);
      setIsModalOpen(true);
    } catch (err) {
      console.error(" Error fetching documents:", err);
      message.error("Failed to load documents.");
    } finally {
      setDocLoading(false);
    }
  };

  // Handlers for View / Download
  // Base file URL (no /api at the end)
  const fileBaseUrl = process.env.REACT_APP_Base_URL;

  //  View document in new tab
  const handlePdfPreview = (fileUrl) => {
    if (!fileUrl) return message.warning("No file found");

    const cleanUrl = `${fileBaseUrl}/${fileUrl.replace(/^\/+/, "")}`;
    window.open(cleanUrl, "_blank");
  };

  //  Download document
  const handleDownload = (fileUrl, fileName) => {
    if (!fileUrl) {
      message.warning("No file to download");
      return;
    }

    // 1. Construct the absolute URL
    const fullUrl = `${fileBaseUrl}/${fileUrl.replace(/^\/+/, "")}`;

    // 2. Create the hidden link
    const link = document.createElement("a");
    link.href = fullUrl;

    // 3. Set the download attribute with the filename + extension
    const extension = fileUrl.split(".").pop() || "pdf";
    link.setAttribute("download", `${fileName}.${extension}`);

    // 4. Force target to _blank (Essential to bypass some CORS block behaviors)
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    // 5. Append, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success("Download link opened!");
  };

  // ---------------- PDF REPORT ----------------
  const handleDownloadOnboardingReport = async (jobApplicantId, name, email) => {
    try {
      const res = await getOnboardingByApplicant(jobApplicantId);

      if (!res?.exists || !res?.data) {
        message.warning("No onboarding data found");
        return;
      }

      generateOnboardingPDF(res, { name, email });
    } catch (error) {
      console.error(error);
    }
  };

  const employeeColumns = [
    {
      title: "Employee Name",
      dataIndex: "firstName",
      key: "firstName",
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button className="btn-primary" onClick={() => handleViewDocuments(record.id)}>
            View Documents
          </Button>

          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              handleDownloadOnboardingReport(
                record.jobApplicantId,
                `${record.firstName} ${record.lastName}`,
                record.email
              )
            }
          >
            Onboarding Report
          </Button>
        </Space>
      ),
    },
  ];

  const documentColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record.file, record.name)}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="body flex-grow-1 px-3">
        <Card title="Employee Received Documents">
          {/* Department Filter */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ marginRight: 10 }}>Select Department:</label>

            <Select
              style={{ width: 300 }}
              placeholder="Choose Department"
              onChange={(value) => {
                setSelectedDept(value);
                fetchEmployees(value, true);
              }}
              value={selectedDept}
            >
              {departments.map((dept) => (
                <Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Option>
              ))}
            </Select>
          </div>

          {/* Employee Table */}
          {loading ? (
            <Spin />
          ) : (
            <Table
              dataSource={employees}
              columns={employeeColumns}
              rowKey="id"
              pagination={false}
            />
          )}
        </Card>

        {/* Documents Modal */}
        <Modal
          title="Employee Documents"
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          width={700}
        >
          {docLoading ? (
            <Spin />
          ) : (
            <Table
              dataSource={documents}
              columns={documentColumns}
              rowKey="name"
              pagination={false}
            />
          )}
        </Modal>
      </div>
    </DashboardLayout>
  );
}

export default ReceivedDocuments;
